import React from 'react';
import { StepStatusByRole, StepStatusPayload } from './interfaces';
import './theme.css';

/**
 * The pipeline's known step order for the flow diagram -- mirrors
 * nfl_training_pipeline.py's real STEP_ROLES mapping for the SUCCESS path
 * (PreProcess -> Train -> Evaluate -> Condition -> FullTrain -> CreateModel
 * -> Register), one node per distinct role, in DAG order. The `fail` role
 * (the Condition step's other branch, taken when the MAPE gate doesn't
 * clear) is deliberately NOT in this list -- this diagram renders a single
 * linear sequence with a connector between every consecutive pair, and
 * `fail` is a branch off `condition_gate`, not a next-in-sequence step; a
 * failed run's overall status still surfaces via the page's "Failed"
 * badge (see Dashboard.tsx's overallStatus), just not as a flow-diagram
 * node. This is currently a hand-maintained mirror of the backend's
 * STEP_ROLES dict -- NOT yet generated from it -- so a future backend
 * step/role change still requires updating this list by hand.
 * Auto-generating this from the backend's step-roles/football-nfl.json
 * build artifact is a deliberate follow-up, not done here: today there is
 * no cross-package build integration between this npm/webpack frontend
 * and the Python backend's Brazil build output, and introducing one is a
 * separate decision from "does showing 7 nodes work."
 *
 * This is display-only ordering, not the source of truth for the
 * pipeline's real DAG (that's the CDK-deployed pipeline definition) -- if
 * a league's real step topology diverges from this list, the diagram will
 * simply not have a node to light up for the extra step, which is a
 * display gap, not a data-correctness issue (the live event stream and
 * past-run archive stay accurate regardless).
 */
const KNOWN_STEPS: Array<{ stepRole: string; label: string }> = [
  { stepRole: 'preprocess', label: 'Preparing data' },
  { stepRole: 'train', label: 'Training model' },
  { stepRole: 'evaluate', label: 'Evaluating accuracy' },
  { stepRole: 'condition_gate', label: 'Accuracy gate' },
  { stepRole: 'full_train', label: 'Training final model' },
  { stepRole: 'create_model', label: 'Creating model' },
  { stepRole: 'register', label: 'Registering model' },
];

type StepDisplayStatus = StepStatusPayload['status'];

function nodeClassName(status: StepDisplayStatus | undefined): 'done' | 'active' | 'failed' | 'pending' {
  switch (status) {
    case 'succeeded':
      return 'done';
    case 'in_progress':
      return 'active';
    case 'failed':
      return 'failed';
    default:
      return 'pending';
  }
}

function connectorClassName(
  leftStatus: StepDisplayStatus | undefined,
  rightStatus: StepDisplayStatus | undefined,
): 'done' | 'active' | '' {
  // Only the gap AFTER the left step finishes is "transitioning toward
  // the next step" -- while the left step is itself still in_progress,
  // this connector is inert (no animation, not green yet). Reported
  // live, 2026-08-05: an earlier version of this function animated the
  // connector whenever the LEFT step was in_progress, so step 2's
  // connector to step 3 was already animating while step 2 itself was
  // still running -- visually implying movement toward step 3 that
  // hadn't started yet. Animation is reserved for leftStatus ===
  // 'succeeded' && rightStatus === undefined (see below), which is the
  // only state where "moving toward the next step" is actually true.
  if (leftStatus === 'succeeded' && rightStatus === undefined) return 'active';
  if (leftStatus === 'succeeded') return 'done';
  return '';
}

function nodeGlyph(status: StepDisplayStatus | undefined, pendingIndex: number): string {
  if (status === 'succeeded') return '\u2713'; // check
  if (status === 'in_progress') return '\u25cf'; // filled dot
  if (status === 'failed') return '\u2715'; // x
  return String(pendingIndex); // pending -- show its position number, matching the mockup
}

interface PipelineFlowDiagramProps {
  /** Latest known status per step_role, from live step_status events. Steps with no entry are rendered as 'pending'. */
  stepStatuses: StepStatusByRole;
  /**
   * Called with a step's role when its node is clicked, for callers that
   * want to show that step's detail (e.g. Dashboard.tsx's StepLogPanel)
   * regardless of whether the step is still running -- lets a user review
   * a finished step's logs, not just the currently-active one. Optional;
   * omit to render a non-interactive diagram (e.g. read-only contexts).
   */
  onStepClick?: (stepRole: string) => void;
}

/**
 * Animated flow diagram matching the approved design mockup
 * (smartwater-mockup-live.html): circular nodes connected by animated
 * connectors, with a pulsing ripple on the active node and a traveling
 * gradient on the active connector. Renders each known step_role as a node
 * that updates as `step_status` events arrive live (pending -> in_progress
 * -> succeeded/failed).
 *
 * The mockup shows a per-step duration once a step completes -- that data
 * (`duration_seconds`) exists on the protocol's StepStatusPayload but
 * `StepStatusByRole` (Dashboard.tsx's live state) only tracks the bare
 * status string today, not the full payload. Omitted here rather than
 * fabricated; wiring duration through is a reasonable fast-follow once
 * Dashboard.tsx's state shape carries it.
 */
export function PipelineFlowDiagram({ stepStatuses, onStepClick }: PipelineFlowDiagramProps) {
  return (
    <div className="flow">
      {KNOWN_STEPS.map(({ stepRole, label }, index) => {
        const status = stepStatuses[stepRole];
        const nextStatus = index < KNOWN_STEPS.length - 1 ? stepStatuses[KNOWN_STEPS[index + 1].stepRole] : undefined;
        return (
          <React.Fragment key={stepRole}>
            <div
              className={`flow-node ${nodeClassName(status)}`}
              data-testid={`flow-step-${stepRole}`}
              onClick={onStepClick ? () => onStepClick(stepRole) : undefined}
              style={onStepClick ? { cursor: 'pointer' } : undefined}
              role={onStepClick ? 'button' : undefined}
            >
              <div className="circle">{nodeGlyph(status, index + 1)}</div>
              <div className="label">{label}</div>
              {status === 'in_progress' && <div className="time">running&hellip;</div>}
            </div>
            {index < KNOWN_STEPS.length - 1 && (
              <div
                className={`flow-connector ${connectorClassName(status, nextStatus)}`}
                data-testid={`flow-connector-${stepRole}-${KNOWN_STEPS[index + 1].stepRole}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
